// BoothLayoutManager Component (Issue #1028 - Phase 2B)
// Visual booth layout editor for Fair Managers to arrange booth positions on a map
// Supports drag-and-drop, bulk CSV upload, shape/rotation controls

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, Save, X, RotateCw, Grid3x3, AlertCircle, Loader2 } from 'lucide-react';
import Papa from 'papaparse';
import BoothMapCanvas from './BoothMapCanvas';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { fairManagerApi } from '../services/api';
import {
  BoothLayout,
  BoothLayoutUpdate,
  BoothLayoutBulkUpload,
  BoothShape,
  Slot,
} from '../types';
import { extractApiError } from '../utils/errorUtils';
import { ROUTES } from '../constants/routes';

interface EditableBoothLayout extends BoothLayout {
  slot_number?: string;
  isSelected?: boolean;
  isDragging?: boolean;
}

interface CSVRow {
  slot_id: string;
  x_coord: string;
  y_coord: string;
  width: string;
  height: string;
  shape: string;
  rotation: string;
}

const BoothLayoutManager: React.FC = () => {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();
  const { isEnabled: boothMapEnabled, isLoading: featureFlagLoading } = useFeatureFlag('booth_map_enabled');

  // State
  const [layouts, setLayouts] = useState<EditableBoothLayout[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedLayoutId, setSelectedLayoutId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Ref to the transformed content group for coordinate conversion
  const contentGroupRef = useRef<SVGGElement>(null);

  // Ref to CSV file input for clearing after upload
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  // Ref to store latest drag position (prevents stale coordinates on auto-save)
  const lastDragPositionRef = useRef<{ x: number; y: number } | null>(null);

  // Fetch booth layouts and slots on mount
  useEffect(() => {
    if (!fairId) return;

    const fetchData = async () => {
      console.debug('[BoothLayoutManager] Fetching booth layouts and slots', { fairId });
      setIsLoading(true);
      setError(null);

      try {
        // Fetch booth layouts (may be empty for new fairs)
        const layoutsResponse = await fairManagerApi.getBoothLayouts(parseInt(fairId));
        console.info('[BoothLayoutManager] Booth layouts loaded', { count: layoutsResponse.length });

        // Fetch slots to get slot numbers
        const slotsResponse = await fairManagerApi.getSlots(parseInt(fairId));
        console.info('[BoothLayoutManager] Slots loaded', { count: slotsResponse.length });

        setSlots(slotsResponse);

        // Merge slot numbers into layouts
        const enrichedLayouts = layoutsResponse.map((layout) => {
          const slot = slotsResponse.find((s) => s.id === layout.slot_id);
          return {
            ...layout,
            slot_number: slot?.slot_number,
            isSelected: false,
            isDragging: false,
          };
        });

        setLayouts(enrichedLayouts);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('[BoothLayoutManager] Failed to load booth layouts', apiError);
        setError(apiError.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [fairId]);

  // Handle booth selection
  const handleBoothClick = (layoutId: number) => {
    console.info('[BoothLayoutManager] Booth selected', { layoutId });
    setSelectedLayoutId(layoutId);
    setLayouts((prev) =>
      prev.map((layout) => ({
        ...layout,
        isSelected: layout.id === layoutId,
      }))
    );
  };

  // Handle booth drag start (supports both mouse and touch)
  const handleBoothDragStart = (e: React.MouseEvent | React.TouchEvent, layoutId: number) => {
    console.debug('[BoothLayoutManager] Booth drag start', { layoutId });
    e.stopPropagation();
    setIsDragging(true);
    setSelectedLayoutId(layoutId);

    const layout = layouts.find((l) => l.id === layoutId);
    if (layout) {
      // Extract client coordinates (mouse or touch)
      const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
      const clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;

      // Calculate offset between pointer and booth top-left corner
      // Use the content group's CTM to account for zoom/pan transform
      const svg = (e.target as SVGElement).ownerSVGElement;
      const contentGroup = contentGroupRef.current;
      if (svg && contentGroup) {
        const ctm = contentGroup.getScreenCTM();
        if (ctm) {
          const pt = svg.createSVGPoint();
          pt.x = clientX;
          pt.y = clientY;
          // Transform screen coordinates to map coordinates using content group's CTM
          const svgP = pt.matrixTransform(ctm.inverse());
          setDragOffset({
            x: svgP.x - layout.x_coord,
            y: svgP.y - layout.y_coord,
          });
        }
      }

      setLayouts((prev) =>
        prev.map((l) => ({
          ...l,
          isDragging: l.id === layoutId,
          isSelected: l.id === layoutId,
        }))
      );
    }
  };

  // Handle booth drag move (supports both mouse and touch)
  const handleBoothDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || selectedLayoutId === null) return;

    // Extract client coordinates (mouse or touch)
    const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
    const clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;

    // Get SVG from contentGroupRef to avoid crashes when cursor leaves SVG
    const contentGroup = contentGroupRef.current;
    const svg = contentGroup?.ownerSVGElement;
    if (!svg || !contentGroup) return;

    // Guard getScreenCTM() to avoid null dereference if SVG is temporarily detached
    const ctm = contentGroup.getScreenCTM();
    if (!ctm) return;

    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    // Transform screen coordinates to map coordinates using content group's CTM
    const svgP = pt.matrixTransform(ctm.inverse());

    // Update layout position (with grid snapping if enabled)
    setLayouts((prev) =>
      prev.map((layout) => {
        if (layout.id === selectedLayoutId) {
          const newX = Math.max(0, svgP.x - dragOffset.x);
          const newY = Math.max(0, svgP.y - dragOffset.y);

          // Snap to grid (10px increments)
          const snappedX = Math.round(newX / 10) * 10;
          const snappedY = Math.round(newY / 10) * 10;

          // Store latest position in ref for auto-save (prevents stale state reads)
          lastDragPositionRef.current = { x: snappedX, y: snappedY };

          return {
            ...layout,
            x_coord: snappedX,
            y_coord: snappedY,
          };
        }
        return layout;
      })
    );
  }, [isDragging, selectedLayoutId, dragOffset]);

  // Save position only (called after drag - doesn't persist unsaved width/height/shape/rotation edits)
  const handleSavePositionOnly = useCallback(async () => {
    if (!fairId || selectedLayoutId === null) return;

    const layout = layouts.find((l) => l.id === selectedLayoutId);
    if (!layout) return;

    console.info('[BoothLayoutManager] Saving booth position only', { layoutId: selectedLayoutId });

    try {
      // Use ref coordinates if available (from recent drag), otherwise use state
      const coords = lastDragPositionRef.current || { x: layout.x_coord, y: layout.y_coord };
      lastDragPositionRef.current = null; // Clear ref after use

      // Only send position - don't persist unsaved edits to width/height/shape/rotation
      const updateData: BoothLayoutUpdate = {
        x_coord: coords.x,
        y_coord: coords.y,
      };

      await fairManagerApi.updateBoothLayout(parseInt(fairId), layout.slot_id, updateData);
      console.info('[BoothLayoutManager] Booth position saved successfully');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('[BoothLayoutManager] Failed to save booth position', apiError);
      setError(apiError.message);
    }
  }, [fairId, selectedLayoutId, layouts]);

  // Handle booth drag end
  const handleBoothDragEnd = useCallback(() => {
    console.debug('[BoothLayoutManager] Booth drag end');

    // Auto-save position only if booth actually moved (prevents unnecessary API writes on click-release)
    if (isDragging && selectedLayoutId !== null && lastDragPositionRef.current !== null) {
      handleSavePositionOnly();
    }

    setIsDragging(false);
    setLayouts((prev) =>
      prev.map((layout) => ({
        ...layout,
        isDragging: false,
      }))
    );
  }, [isDragging, selectedLayoutId, handleSavePositionOnly]);

  // Attach global mouse/touch event listeners for drag (prevents losing drag when pointer leaves element)
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const mouseEvent = e as unknown as React.MouseEvent<SVGSVGElement>;
        handleBoothDragMove(mouseEvent);
      };

      const handleGlobalTouchMove = (e: TouchEvent) => {
        const touchEvent = e as unknown as React.TouchEvent<SVGSVGElement>;
        handleBoothDragMove(touchEvent);
      };

      const handleGlobalEnd = () => {
        handleBoothDragEnd();
      };

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalEnd);
      window.addEventListener('touchmove', handleGlobalTouchMove);
      window.addEventListener('touchend', handleGlobalEnd);
      window.addEventListener('touchcancel', handleGlobalEnd);

      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalEnd);
        window.removeEventListener('touchmove', handleGlobalTouchMove);
        window.removeEventListener('touchend', handleGlobalEnd);
        window.removeEventListener('touchcancel', handleGlobalEnd);
      };
    }
  }, [isDragging, selectedLayoutId, dragOffset, handleBoothDragMove, handleBoothDragEnd]);

  // Handle booth shape change
  const handleShapeChange = (shape: BoothShape) => {
    if (selectedLayoutId === null) return;

    console.info('[BoothLayoutManager] Booth shape changed', { layoutId: selectedLayoutId, shape });
    setLayouts((prev) =>
      prev.map((layout) =>
        layout.id === selectedLayoutId ? { ...layout, shape } : layout
      )
    );
  };

  // Handle booth rotation
  const handleRotationChange = (rotation: number) => {
    if (selectedLayoutId === null) return;

    console.info('[BoothLayoutManager] Booth rotation changed', { layoutId: selectedLayoutId, rotation });
    setLayouts((prev) =>
      prev.map((layout) => {
        if (layout.id === selectedLayoutId) {
          // Ensure rotation stays in 0-360 range (handle negative modulo correctly)
          const newRotation = ((layout.rotation + rotation) % 360 + 360) % 360;
          return { ...layout, rotation: newRotation };
        }
        return layout;
      })
    );
  };

  // Handle booth dimension change
  const handleDimensionChange = (dimension: 'width' | 'height', value: number) => {
    if (selectedLayoutId === null) return;

    // Guard against NaN when input is cleared or invalid
    if (isNaN(value) || value === null || value === undefined) {
      console.warn('[BoothLayoutManager] Invalid dimension value (NaN), ignoring', { dimension, value });
      return;
    }

    console.info('[BoothLayoutManager] Booth dimension changed', { layoutId: selectedLayoutId, dimension, value });
    setLayouts((prev) =>
      prev.map((layout) =>
        layout.id === selectedLayoutId ? { ...layout, [dimension]: Math.max(10, value) } : layout
      )
    );
  };

  // Save full booth layout (width, height, shape, rotation, position)
  const handleSaveLayout = async () => {
    if (!fairId || selectedLayoutId === null) return;

    const layout = layouts.find((l) => l.id === selectedLayoutId);
    if (!layout) return;

    console.info('[BoothLayoutManager] Saving full booth layout', { layoutId: selectedLayoutId });
    setIsSaving(true);
    setError(null);

    try {
      // Use ref coordinates if available (from recent drag), otherwise use state
      const coords = lastDragPositionRef.current || { x: layout.x_coord, y: layout.y_coord };
      lastDragPositionRef.current = null; // Clear ref after use

      const updateData: BoothLayoutUpdate = {
        x_coord: coords.x,
        y_coord: coords.y,
        width: layout.width,
        height: layout.height,
        shape: layout.shape,
        rotation: layout.rotation,
      };

      await fairManagerApi.updateBoothLayout(parseInt(fairId), layout.slot_id, updateData);
      console.info('[BoothLayoutManager] Booth layout saved successfully');
      setSuccessMessage('Booth layout saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('[BoothLayoutManager] Failed to save booth layout', apiError);
      setError(apiError.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle CSV file upload
  const handleCSVUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.info('[BoothLayoutManager] CSV file selected', { fileName: file.name, size: file.size });

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      setError('CSV file size exceeds 1MB limit');
      if (csvFileInputRef.current) {
        csvFileInputRef.current.value = ''; // Clear input to allow re-selection
      }
      return;
    }

    setIsUploading(true);
    setError(null);
    setWarning(null);

    Papa.parse<CSVRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        console.debug('[BoothLayoutManager] CSV parsed', { rows: results.data.length });

        // Validate CSV format
        if (results.data.length === 0) {
          setError('CSV file is empty');
          setIsUploading(false);
          if (csvFileInputRef.current) {
            csvFileInputRef.current.value = ''; // Clear input to allow re-selection
          }
          return;
        }

        // Validate row limit (max 1000 booths)
        if (results.data.length > 1000) {
          setError('CSV file exceeds 1000 row limit');
          setIsUploading(false);
          if (csvFileInputRef.current) {
            csvFileInputRef.current.value = ''; // Clear input to allow re-selection
          }
          return;
        }

        // Validate and parse rows
        const validationErrors: string[] = [];
        const parsedLayouts = results.data
          .map((row, index) => {
            const rowNumber = index + 1;
            const slotId = parseInt(row.slot_id);
            const xCoord = parseFloat(row.x_coord);
            const yCoord = parseFloat(row.y_coord);
            const width = parseFloat(row.width);
            const height = parseFloat(row.height);
            const shape = row.shape as BoothShape;
            const rotation = parseFloat(row.rotation);

            // Validate slot ID exists
            const slot = slots.find((s) => s.id === slotId);
            if (!slot) {
              const errorMsg = `Row ${rowNumber}: Invalid slot ID ${slotId}`;
              console.warn(`[BoothLayoutManager] CSV ${errorMsg}`);
              validationErrors.push(errorMsg);
              return null;
            }

            // Validate numeric fields
            if (isNaN(xCoord) || isNaN(yCoord) || isNaN(width) || isNaN(height) || isNaN(rotation)) {
              const errorMsg = `Row ${rowNumber}: Invalid numeric values (x=${row.x_coord}, y=${row.y_coord}, w=${row.width}, h=${row.height}, rot=${row.rotation})`;
              console.warn(`[BoothLayoutManager] CSV ${errorMsg}`);
              validationErrors.push(errorMsg);
              return null;
            }

            // Validate shape
            if (!['rectangle', 'circle', 'polygon'].includes(shape)) {
              const errorMsg = `Row ${rowNumber}: Invalid shape "${shape}" (must be rectangle, circle, or polygon)`;
              console.warn(`[BoothLayoutManager] CSV ${errorMsg}`);
              validationErrors.push(errorMsg);
              return null;
            }

            // Validate rotation (0-360)
            if (rotation < 0 || rotation > 360) {
              const errorMsg = `Row ${rowNumber}: Invalid rotation ${rotation} (must be 0-360)`;
              console.warn(`[BoothLayoutManager] CSV ${errorMsg}`);
              validationErrors.push(errorMsg);
              return null;
            }

            return {
              slot_id: slotId,
              x_coord: xCoord,
              y_coord: yCoord,
              width,
              height,
              shape,
              rotation,
            };
          })
          .filter((layout) => layout !== null) as Array<{
            slot_id: number;
            x_coord: number;
            y_coord: number;
            width: number;
            height: number;
            shape: BoothShape;
            rotation: number;
          }>;

        console.info('[BoothLayoutManager] CSV validation complete', {
          total: results.data.length,
          valid: parsedLayouts.length,
          rejected: validationErrors.length,
        });

        if (parsedLayouts.length === 0) {
          // Show detailed error summary if all rows were rejected
          const errorSummary = validationErrors.length > 0
            ? `No valid booth layouts found. ${validationErrors.length} row(s) rejected:\n${validationErrors.slice(0, 5).join('\n')}${validationErrors.length > 5 ? `\n... and ${validationErrors.length - 5} more` : ''}`
            : 'No valid booth layouts found in CSV file';
          setError(errorSummary);
          setIsUploading(false);
          if (csvFileInputRef.current) {
            csvFileInputRef.current.value = ''; // Clear input to allow re-selection
          }
          return;
        }

        // If some rows were rejected but we have valid ones, show a warning
        if (validationErrors.length > 0) {
          const warningMsg = `Uploaded ${parsedLayouts.length} booth layout(s). ${validationErrors.length} row(s) rejected:\n${validationErrors.slice(0, 5).join('\n')}${validationErrors.length > 5 ? `\n... and ${validationErrors.length - 5} more` : ''}`;
          setWarning(warningMsg);
        } else {
          setWarning(null); // Clear warning if no validation errors
        }

        // Upload bulk layouts
        uploadBulkLayouts(parsedLayouts);
      },
      error: (error) => {
        console.error('[BoothLayoutManager] CSV parse error', error);
        setError(`CSV parse error: ${error.message}`);
        setIsUploading(false);
        if (csvFileInputRef.current) {
          csvFileInputRef.current.value = ''; // Clear input to allow re-selection
        }
      },
    });
  };

  // Upload bulk booth layouts
  const uploadBulkLayouts = async (
    parsedLayouts: Array<{
      slot_id: number;
      x_coord: number;
      y_coord: number;
      width: number;
      height: number;
      shape: BoothShape;
      rotation: number;
    }>
  ) => {
    if (!fairId) return;

    console.info('[BoothLayoutManager] Uploading bulk booth layouts', { count: parsedLayouts.length });

    try {
      const bulkData: BoothLayoutBulkUpload = {
        fair_id: parseInt(fairId),
        layouts: parsedLayouts,
      };

      const response = await fairManagerApi.bulkUploadBoothLayouts(parseInt(fairId), bulkData);
      console.info('[BoothLayoutManager] Bulk upload successful', response);
      setSuccessMessage(`Bulk upload successful: ${response.created} created, ${response.updated} updated`);
      setTimeout(() => setSuccessMessage(null), 5000);
      // Note: Don't clear warning here - it shows which rows were rejected during validation

      // Reload layouts
      const layoutsResponse = await fairManagerApi.getBoothLayouts(parseInt(fairId));
      const enrichedLayouts = layoutsResponse.map((layout) => {
        const slot = slots.find((s) => s.id === layout.slot_id);
        return {
          ...layout,
          slot_number: slot?.slot_number,
          isSelected: false,
          isDragging: false,
        };
      });
      setLayouts(enrichedLayouts);

      // Clear CSV file input to allow re-selecting the same file (useful for fixing CSV errors)
      if (csvFileInputRef.current) {
        csvFileInputRef.current.value = '';
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('[BoothLayoutManager] Bulk upload failed', apiError);
      setError(apiError.message);
    } finally {
      setIsUploading(false);
    }
  };

  // Render loading state
  if (featureFlagLoading || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  // Render feature flag disabled state
  if (!boothMapEnabled) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Booth map features are not yet available.</p>
        <button
          onClick={() => navigate(ROUTES.FAIR_MANAGER.FAIRS)}
          className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
        >
          Back to Fairs List
        </button>
      </div>
    );
  }

  const selectedLayout = layouts.find((l) => l.id === selectedLayoutId);

  return (
    <div className="booth-layout-manager container mx-auto px-4 py-8" data-testid="booth-layout-manager-container">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="page-title">
            Booth Layout Manager
          </h1>
          <p className="text-gray-600 mt-1">
            Drag booths to position them on the map. Use controls to adjust shape, size, and rotation.
          </p>
        </div>
        <button
          onClick={() => navigate(ROUTES.FAIR_MANAGER.FAIRS)}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-2"
          data-testid="back-button"
        >
          <X className="h-4 w-4" />
          Back to Fairs List
        </button>
      </div>

      {/* Error/Warning/Success Messages */}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-start gap-2" data-testid="error-message">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="whitespace-pre-line">{error}</span>
        </div>
      )}
      {warning && (
        <div className="mb-4 bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded flex items-start gap-2" data-testid="warning-message">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="whitespace-pre-line">{warning}</span>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded" data-testid="success-message">
          {successMessage}
        </div>
      )}

      {/* CSV Upload */}
      <div className="mb-6 bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Bulk Upload Booth Layouts (CSV)
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Format: slot_id, x_coord, y_coord, width, height, shape, rotation
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Max 1MB file size, 1000 rows. Shape: rectangle, circle, or polygon. Rotation: 0-360.
            </p>
          </div>
          <label className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark cursor-pointer flex items-center gap-2" data-testid="bulk-upload-csv-button">
            <Upload className="h-4 w-4" />
            {isUploading ? 'Uploading...' : 'Upload CSV'}
            <input
              ref={csvFileInputRef}
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              disabled={isUploading}
              className="hidden"
              data-testid="csv-upload-input"
            />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Booth Map Canvas */}
        <div className="lg:col-span-3">
          <BoothMapCanvas
            editMode
            showGrid
            showControls
            className="mb-4"
            contentGroupRef={contentGroupRef}
          >
            {/* Render booths */}
            {layouts.map((layout) => (
              <g
                key={layout.id}
                transform={`translate(${layout.x_coord}, ${layout.y_coord}) rotate(${layout.rotation}, ${layout.width / 2}, ${layout.height / 2})`}
                onMouseDown={(e) => handleBoothDragStart(e, layout.id)}
                onTouchStart={(e) => handleBoothDragStart(e, layout.id)}
                onClick={() => handleBoothClick(layout.id)}
                className="booth-shape cursor-move"
                data-slot-id={layout.slot_id}
                data-testid={`booth-${layout.id}`}
              >
                {/* Booth shape */}
                {layout.shape === 'rectangle' && (
                  <rect
                    width={layout.width}
                    height={layout.height}
                    fill={layout.isSelected ? '#3B82F6' : '#10B981'}
                    stroke={layout.isSelected ? '#1F2937' : '#374151'}
                    strokeWidth={layout.isSelected ? 3 : 1}
                    opacity={layout.isDragging ? 0.6 : 0.8}
                    className="transition-all hover:opacity-100"
                  />
                )}
                {layout.shape === 'circle' && (
                  <circle
                    cx={layout.width / 2}
                    cy={layout.height / 2}
                    r={Math.min(layout.width, layout.height) / 2}
                    fill={layout.isSelected ? '#3B82F6' : '#10B981'}
                    stroke={layout.isSelected ? '#1F2937' : '#374151'}
                    strokeWidth={layout.isSelected ? 3 : 1}
                    opacity={layout.isDragging ? 0.6 : 0.8}
                    className="transition-all hover:opacity-100"
                  />
                )}
                {layout.shape === 'polygon' && (
                  <polygon
                    points={`0,${layout.height / 2} ${layout.width / 4},0 ${(3 * layout.width) / 4},0 ${layout.width},${layout.height / 2} ${(3 * layout.width) / 4},${layout.height} ${layout.width / 4},${layout.height}`}
                    fill={layout.isSelected ? '#3B82F6' : '#10B981'}
                    stroke={layout.isSelected ? '#1F2937' : '#374151'}
                    strokeWidth={layout.isSelected ? 3 : 1}
                    opacity={layout.isDragging ? 0.6 : 0.8}
                    className="transition-all hover:opacity-100"
                  />
                )}

                {/* Booth label */}
                <text
                  x={layout.width / 2}
                  y={layout.height / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={14}
                  fontWeight="bold"
                  fill="white"
                  className="pointer-events-none"
                >
                  {layout.slot_number || layout.slot_id}
                </text>
              </g>
            ))}
          </BoothMapCanvas>

          {layouts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No booth layouts found. Upload a CSV file to get started.</p>
            </div>
          )}
        </div>

        {/* Booth Editor Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-4 sticky top-4" data-testid="booth-layout-controls">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Booth Editor</h2>

            {selectedLayout ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booth: {selectedLayout.slot_number || selectedLayout.slot_id}
                  </label>
                </div>

                {/* Shape selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Shape</label>
                  <div className="grid grid-cols-2 gap-2" data-testid="booth-shape-select">
                    <button
                      onClick={() => handleShapeChange('rectangle')}
                      className={`px-3 py-2 rounded border ${
                        selectedLayout.shape === 'rectangle'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid="shape-rectangle"
                      value="rectangle"
                    >
                      Rectangle
                    </button>
                    <button
                      onClick={() => handleShapeChange('circle')}
                      className={`px-3 py-2 rounded border ${
                        selectedLayout.shape === 'circle'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid="shape-circle"
                      value="square"
                    >
                      Circle
                    </button>
                    <button
                      onClick={() => handleShapeChange('polygon')}
                      className={`px-3 py-2 rounded border ${
                        selectedLayout.shape === 'polygon'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid="shape-polygon"
                      value="corner"
                    >
                      Polygon
                    </button>
                  </div>
                </div>

                {/* Dimensions */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                    <input
                      type="number"
                      value={selectedLayout.width}
                      onChange={(e) => handleDimensionChange('width', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                      min="10"
                      data-testid="width-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
                    <input
                      type="number"
                      value={selectedLayout.height}
                      onChange={(e) => handleDimensionChange('height', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                      min="10"
                      data-testid="height-input"
                    />
                  </div>
                </div>

                {/* Rotation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rotation: {selectedLayout.rotation}°
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRotationChange(-15)}
                      className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center justify-center gap-1"
                      data-testid="rotate-left-button"
                    >
                      <RotateCw className="h-4 w-4 rotate-180" />
                      -15°
                    </button>
                    <button
                      onClick={() => handleRotationChange(15)}
                      className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center justify-center gap-1"
                      data-testid="rotate-right-button"
                    >
                      <RotateCw className="h-4 w-4" />
                      +15°
                    </button>
                  </div>
                </div>

                {/* Position */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">X Position</label>
                    <input
                      type="number"
                      value={Math.round(selectedLayout.x_coord)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                      data-testid="x-coord"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Y Position</label>
                    <input
                      type="number"
                      value={Math.round(selectedLayout.y_coord)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                      data-testid="y-coord"
                    />
                  </div>
                </div>

                {/* Save button */}
                <button
                  onClick={handleSaveLayout}
                  disabled={isSaving}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                  data-testid="save-layout-button"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Layout'}
                </button>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Grid3x3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Select a booth to edit</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoothLayoutManager;
